import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Subheading } from "catalyst-ui-kit/heading";
import { PurchaseDocDetail, PurchaseSupplierIbanAction } from "types/InvoiceInbox";
import { linkOrCreateSupplier } from "services/invoice-inbox.service";
import { fetchById as fetchCustomerById } from "services/customers.service";
import CustomerRegistrySearch, { RegistryMatch } from "features/customer-registry-search/customer-registry-search";
import { fillIfEmpty } from "features/customer-form/fill-if-empty";

/**
 * The form behind the doc panel's "Create supplier" button.
 *
 * Seeded from the document's raw fields, but every one of them stays
 * editable: extraction gets a company code or a VAT code wrong often enough
 * that saving it blind is not acceptable — this is what a customer master is
 * built from.
 *
 * Two outcomes, one submit: a fresh customer, or — if the VAT/company code
 * already belongs to one — linking to it instead (server-checked, not
 * guessed here, because the registry search's own duplicate check is
 * best-effort and must not be the only guard).
 *
 * Not catalyst-ui-kit form controls: they are Tailwind-4-styled and render
 * broken under this project's Tailwind 3 (see invoice-inbox-settings.tsx).
 */

const inputCls =
  "w-full rounded-lg border border-zinc-300 bg-transparent px-3 py-1.5 text-base md:text-sm outline-none transition-all duration-150 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-700 dark:focus:ring-indigo-400/20 dark:focus:border-indigo-400";
const btnCls =
  "rounded-lg border border-zinc-200/80 bg-transparent px-3 py-1.5 text-sm font-medium hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900 transition-colors duration-150 disabled:opacity-40 max-md:min-h-11";
const primaryBtnCls =
  "rounded-lg bg-indigo-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 active:bg-indigo-700 shadow-sm transition-colors duration-150 disabled:opacity-40 max-md:min-h-11 dark:bg-indigo-500 dark:hover:bg-indigo-400";

type Conflict = {
  id: string;
  name: string;
  legalName: string | null;
  vatCode: string | null;
  companyCode: string | null;
  /** Why the 409 picked this customer — shown so "already on file" reads as
   * an explanation rather than an accusation. */
  matchedBy?: "vat_code" | "document_vat_code" | "company_code";
};

/** The card about to be linked, and what its PRIMARY account currently
 * prints — fetched once, in beginLink, so the "make it primary" step can
 * hint at it without a second round trip. */
type LinkChoice = {
  customerName: string;
  customerId: string;
  primaryBankName: string | null;
  primarySwiftCode: string | null;
};

type Props = {
  companyId: string;
  detail: PurchaseDocDetail;
  /** Whether this user may set how the supplier's future invoices are approved. */
  canManageSettings: boolean;
  onCancel: () => void;
  onCreated: () => void;
};

const normalizeIban = (value: string | null | undefined) =>
  (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

// Which of THIS form's fields mirrors a doc field the backend could not
// verify — the amber hint lives on the doc field name, ours are different.
const REVIEW_FIELD_MAP: Record<string, string> = {
  name: "supplierNameRaw",
  companyCode: "supplierRegCodeRaw",
  vatCode: "supplierVatCodeRaw",
  iban: "supplierIbanRaw",
};

const IBAN_ACTIONS: PurchaseSupplierIbanAction[] = ["ignore", "add", "replace"];

export default function InvoiceSupplierCreate({ companyId, detail, canManageSettings, onCancel, onCreated }: Props) {
  const { t } = useTranslation();
  const doc = detail.doc;
  const asText = (value: string | null | undefined) => value || "";

  // The server cuts "UAB Tiekėjas" into the name and its legal form.
  const [name, setName] = useState(detail.supplierNameSplit.name);
  const [legalName, setLegalName] = useState(asText(detail.supplierNameSplit.legalForm));
  const [companyCode, setCompanyCode] = useState(asText(doc.supplierRegCodeRaw));
  const [vatCode, setVatCode] = useState(asText(doc.supplierVatCodeRaw));
  const [country, setCountry] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  // A supplier with a VAT code is a VAT payer. The box follows the VAT code
  // field until the person ticks it themselves; after that it is theirs.
  const [isVatPayer, setIsVatPayer] = useState(Boolean(vatCode.trim()));
  const [vatPayerEditedByHand, setVatPayerEditedByHand] = useState(false);
  const [invoicesArrivePaid, setInvoicesArrivePaid] = useState(false);
  // The document's IBAN is prefilled and saved with a NEW customer without
  // asking again — that question only exists when LINKING to one that
  // already has different accounts on file.
  const [iban, setIban] = useState(asText(doc.supplierIbanRaw));
  const [bankName, setBankName] = useState("");
  const [swiftCode, setSwiftCode] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [linkChoice, setLinkChoice] = useState<LinkChoice | null>(null);
  const [ibanAction, setIbanAction] = useState<PurchaseSupplierIbanAction>("ignore");
  // Only asked about when "replace" is chosen — the new account is usually a
  // different bank, so these start blank rather than copying the old values.
  const [linkBankName, setLinkBankName] = useState("");
  const [linkSwiftCode, setLinkSwiftCode] = useState("");
  const [bankFieldsError, setBankFieldsError] = useState<string | null>(null);

  const needsReview = doc.needsReviewFields || [];
  const unverifiedHint = (key: string) =>
    needsReview.indexOf(REVIEW_FIELD_MAP[key] || key) !== -1 ? (
      <span className="mt-1 block text-xs text-amber-700">
        {t("invoice_inbox_field_unverified", { defaultValue: "Could not be verified automatically" })}
      </span>
    ) : null;

  /**
   * Only fields the register is authoritative for — mirrors
   * customer-form.jsx's applyRegistryMatch. Bank details are never touched
   * by a registry pick: no register knows them.
   */
  const applyRegistryMatch = (match: RegistryMatch, opts?: { addressOnly?: boolean }) => {
    if (opts?.addressOnly) {
      // Late answer: it must not overwrite an address typed meanwhile.
      setAddress((current) => fillIfEmpty(current, match.address));
      return;
    }
    setName(match.name || "");
    setLegalName(match.legalName || "");
    setCompanyCode(match.companyCode || "");
    setVatCode(match.vatCodeFigures ? `${match.vatCodeLetters}${match.vatCodeFigures}` : "");
    setCountry(match.country || match.vatCodeLetters || "");
    setAddress(match.address || "");
    setIsVatPayer(Boolean(match.isVatPayer));
    // The register has answered: a later edit of the VAT code must not overwrite it.
    setVatPayerEditedByHand(true);
  };

  const changeVatCode = (value: string) => {
    setVatCode(value);
    if (!vatPayerEditedByHand) setIsVatPayer(Boolean(value.trim()));
  };

  const errorText = (e: any): string => {
    const data = e?.response?.data;
    if (data?.code === "SETTINGS_PERMISSION_REQUIRED") {
      return t("invoice_inbox_supplier_settings_permission_required", {
        defaultValue: "Setting that invoices arrive paid needs the invoice inbox settings right.",
      });
    }
    if (data?.code === "DOC_DELETED") {
      return t("invoice_inbox_supplier_doc_deleted", { defaultValue: "This document has been removed." });
    }
    if (data?.code === "SUPPLIER_ALREADY_LINKED") {
      return t("invoice_inbox_supplier_already_linked", {
        defaultValue: "This document already has a linked supplier.",
      });
    }
    return data?.message || t("invoice_inbox_action_failed", { defaultValue: "The action did not go through." });
  };

  // The 400 form of SUPPLIER_ALREADY_LINKED means "linked to someone else,
  // stop" — errorText's wording is right for that. The 409 form means WE lost
  // a race that was ours to win: somebody linked it a moment before this
  // request landed. That is not a mistake to report, it is news to catch up
  // on — reload and close rather than show an error the user did nothing wrong to see.
  const isLostLinkRace = (e: any): boolean =>
    e?.response?.status === 409 && e?.response?.data?.code === "SUPPLIER_ALREADY_LINKED";

  const submitCreate = async () => {
    setBusy(true);
    setError(null);
    setConflict(null);
    try {
      await linkOrCreateSupplier(companyId, doc.id, {
        name: name.trim(),
        legalName: legalName.trim() || undefined,
        companyCode: companyCode.trim(),
        vatCode: vatCode.trim() || undefined,
        country: country.trim() || undefined,
        address: address.trim() || undefined,
        email: email.trim() || undefined,
        isVatPayer,
        iban: iban.trim() || undefined,
        bankName: bankName.trim() || undefined,
        swiftCode: swiftCode.trim() || undefined,
        invoicesArrivePaid: invoicesArrivePaid || undefined,
      });
      onCreated();
    } catch (e: any) {
      if (isLostLinkRace(e)) {
        onCreated();
        return;
      }
      const data = e?.response?.data;
      if (data?.code === "CUSTOMER_EXISTS" && data.customer) {
        setConflict({ ...data.customer, matchedBy: data.matchedBy });
      } else {
        setError(errorText(e));
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * "Link it" from the conflict banner. The 409 only carries the customer's
   * identity, not its accounts, so the full record is fetched here to
   * compare IBANs — the three-way choice is only worth asking when the
   * document's account is genuinely not on that card yet. The same fetch
   * hands back `bankName` / `bankSwiftCode` — the card's mirror of its
   * PRIMARY account — so the "make it primary" step can hint at what is
   * there now without a second round trip.
   */
  const beginLink = async () => {
    if (!conflict) return;
    setBusy(true);
    setError(null);
    try {
      const docIban = normalizeIban(doc.supplierIbanRaw);
      let onFile = true;
      let customer: any = null;
      if (docIban) {
        customer = await fetchCustomerById(companyId, conflict.id);
        const ibans: string[] = (
          customer?.bankAccounts?.length
            ? customer.bankAccounts.map((a: any) => a.iban)
            : [customer?.bankIban]
        ).filter(Boolean);
        onFile = ibans.some((value) => normalizeIban(value) === docIban);
      }
      if (onFile) {
        await linkOrCreateSupplier(companyId, doc.id, { customerId: conflict.id });
        onCreated();
      } else {
        setIbanAction("ignore");
        setLinkBankName("");
        setLinkSwiftCode("");
        setBankFieldsError(null);
        setLinkChoice({
          customerId: conflict.id,
          customerName: conflict.name,
          primaryBankName: customer?.bankName || null,
          primarySwiftCode: customer?.bankSwiftCode || null,
        });
        // The conflict panel renders first, so leaving it set would hide the
        // step it just opened: linking a known supplier whose card lacks this
        // account would look like nothing happened (bugbot #694).
        setConflict(null);
      }
    } catch (e: any) {
      if (isLostLinkRace(e)) {
        onCreated();
        return;
      }
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmLink = async () => {
    if (!linkChoice) return;
    setBusy(true);
    setError(null);
    setBankFieldsError(null);
    try {
      await linkOrCreateSupplier(companyId, doc.id, {
        customerId: linkChoice.customerId,
        ibanAction,
        bankName: linkBankName.trim() || undefined,
        swiftCode: linkSwiftCode.trim() || undefined,
      });
      onCreated();
    } catch (e: any) {
      if (isLostLinkRace(e)) {
        onCreated();
        return;
      }
      const data = e?.response?.data;
      if (data?.code === "BANK_DETAILS_REQUIRED") {
        setBankFieldsError(
          t("invoice_inbox_supplier_bank_details_required", {
            defaultValue:
              "Making this account primary would erase the bank name and SWIFT code printed on this customer's contracts and orders — enter them to confirm, or pick a different option above.",
          })
        );
        return;
      }
      if (data?.code === "TOO_MANY_ACCOUNTS") {
        setError(
          t("invoice_inbox_supplier_too_many_accounts", {
            defaultValue: "This customer already has the maximum of 20 bank accounts on file — nothing was changed.",
          })
        );
        return;
      }
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-zinc-200/80 p-4 dark:border-zinc-800">
      <Subheading className="!mb-3 !font-semibold">
        {t("invoice_inbox_supplier_form_title", { defaultValue: "Create or link the supplier" })}
      </Subheading>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      {conflict ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <span className="flex items-center gap-1.5 font-medium">
            <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
            {t("invoice_inbox_supplier_conflict", { defaultValue: "This counterparty is already on file" })}
          </span>
          <span className="mt-0.5 block">
            {conflict.name}
            {conflict.companyCode ? ` · ${conflict.companyCode}` : ""}
            {conflict.vatCode ? ` · ${conflict.vatCode}` : ""}
          </span>
          {conflict.matchedBy && (
            <span className="mt-0.5 block text-xs text-amber-800/80 dark:text-amber-300/70">
              {t(
                conflict.matchedBy === "vat_code"
                  ? "invoice_inbox_supplier_matched_vat_code"
                  : conflict.matchedBy === "document_vat_code"
                    ? "invoice_inbox_supplier_matched_document_vat_code"
                    : "invoice_inbox_supplier_matched_company_code",
                {
                  defaultValue:
                    conflict.matchedBy === "vat_code"
                      ? "Matched by this counterparty's VAT code."
                      : conflict.matchedBy === "document_vat_code"
                        ? "Matched by the VAT code on this document."
                        : "Matched by its company code.",
                }
              )}
            </span>
          )}
          <div className="mt-2 flex gap-2">
            <button type="button" className={primaryBtnCls} disabled={busy} onClick={beginLink}>
              {t("invoice_inbox_supplier_link_it", { defaultValue: "Link it" })}
            </button>
            <button type="button" className={btnCls} disabled={busy} onClick={() => setConflict(null)}>
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
          </div>
        </div>
      ) : linkChoice ? (
        <div className="space-y-3">
          <div className="flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              {t("invoice_inbox_supplier_iban_warning", {
                defaultValue: "The account on this document is not on that customer's card yet.",
              })}{" "}
              <strong>{linkChoice.customerName}</strong>
            </span>
          </div>
          <div className="space-y-1.5 text-sm">
            {IBAN_ACTIONS.map((option) => (
              <label key={option} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="supplier-iban-action"
                  checked={ibanAction === option}
                  onChange={() => {
                    setIbanAction(option);
                    setBankFieldsError(null);
                  }}
                />
                {t(`invoice_inbox_supplier_iban_${option}`, {
                  defaultValue:
                    option === "ignore"
                      ? "Leave the card as it is"
                      : option === "add"
                        ? "Add this account"
                        : "Make it the primary account",
                })}
              </label>
            ))}
          </div>
          {ibanAction === "replace" && (
            <div className="space-y-2 rounded-lg border border-zinc-200/80 p-3 dark:border-zinc-800">
              <p className="text-xs text-zinc-500">
                {t("invoice_inbox_supplier_primary_bank_hint", {
                  defaultValue:
                    "This customer's contracts and orders print the primary account's bank — the new account is usually at a different bank, so confirm or retype it below.",
                })}
              </p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                    {t("invoice_inbox_supplier_bank_name", { defaultValue: "Bank" })}
                  </span>
                  <input
                    className={inputCls}
                    value={linkBankName}
                    onChange={(e) => setLinkBankName(e.target.value)}
                    placeholder={linkChoice.primaryBankName || ""}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                    {t("bank_swift", { defaultValue: "SWIFT code" })}
                  </span>
                  <input
                    className={inputCls}
                    value={linkSwiftCode}
                    onChange={(e) => setLinkSwiftCode(e.target.value)}
                    placeholder={linkChoice.primarySwiftCode || ""}
                  />
                </label>
              </div>
              {bankFieldsError && (
                <p className="text-xs text-red-700 dark:text-red-400">{bankFieldsError}</p>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" className={primaryBtnCls} disabled={busy} onClick={confirmLink}>
              {t("confirm", { defaultValue: "Confirm" })}
            </button>
            <button
              type="button"
              className={btnCls}
              disabled={busy}
              onClick={() => {
                setLinkChoice(null);
                setLinkBankName("");
                setLinkSwiftCode("");
                setBankFieldsError(null);
              }}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
          </div>
        </div>
      ) : (
        <>
          <CustomerRegistrySearch companyId={companyId} onSelect={applyRegistryMatch} />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {t("supplier", { defaultValue: "Supplier" })}
              </span>
              <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
              {unverifiedHint("name")}
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {t("invoice_inbox_supplier_legal_form", { defaultValue: "Legal form" })}
              </span>
              <input className={inputCls} value={legalName} onChange={(e) => setLegalName(e.target.value)} />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {t("company_code", { defaultValue: "Company code" })}
              </span>
              <input className={inputCls} value={companyCode} onChange={(e) => setCompanyCode(e.target.value)} />
              {unverifiedHint("companyCode")}
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {t("vat_code", { defaultValue: "VAT code" })}
              </span>
              <input className={inputCls} value={vatCode} onChange={(e) => changeVatCode(e.target.value)} />
              {unverifiedHint("vatCode")}
            </label>

            <label className="flex items-center gap-2 md:col-span-2">
              <input
                type="checkbox"
                checked={isVatPayer}
                onChange={(e) => {
                  setIsVatPayer(e.target.checked);
                  setVatPayerEditedByHand(true);
                }}
              />
              <span className="text-sm">
                {t("invoice_inbox_supplier_vat_payer", { defaultValue: "VAT payer" })}
              </span>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {t("country_of_registration", { defaultValue: "Country (of registration)" })}
              </span>
              <input className={inputCls} value={country} onChange={(e) => setCountry(e.target.value)} />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {t("address", { defaultValue: "Address" })}
              </span>
              <input className={inputCls} value={address} onChange={(e) => setAddress(e.target.value)} />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {t("email", { defaultValue: "Email" })}
              </span>
              <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {t("iban", { defaultValue: "IBAN" })}
              </span>
              <input className={inputCls} value={iban} onChange={(e) => setIban(e.target.value)} />
              {unverifiedHint("iban")}
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {t("invoice_inbox_supplier_bank_name", { defaultValue: "Bank" })}
              </span>
              <input className={inputCls} value={bankName} onChange={(e) => setBankName(e.target.value)} />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {t("bank_swift", { defaultValue: "SWIFT code" })}
              </span>
              <input className={inputCls} value={swiftCode} onChange={(e) => setSwiftCode(e.target.value)} />
            </label>
          </div>

          {canManageSettings && (
            <label className="mt-3 flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={invoicesArrivePaid}
                onChange={(e) => setInvoicesArrivePaid(e.target.checked)}
              />
              <span className="text-sm">
                {t("invoice_inbox_supplier_invoices_arrive_paid", {
                  defaultValue: "Invoices from this supplier arrive already paid",
                })}
                <span className="block text-xs text-zinc-500">
                  {t("invoice_inbox_supplier_create_arrive_paid_help", {
                    defaultValue:
                      "From now on the manager's approval step is skipped for this supplier's invoices — the accountant's check still applies.",
                  })}
                </span>
              </span>
            </label>
          )}

          <p className="mt-2 text-xs text-zinc-500">
            {t("invoice_inbox_supplier_iban_note", {
              defaultValue:
                "Saved with the supplier — a new bank account still has to be confirmed separately before this document can be paid.",
            })}
          </p>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className={primaryBtnCls}
              disabled={busy || !name.trim() || !companyCode.trim()}
              onClick={submitCreate}
            >
              {t("invoice_inbox_supplier_submit", { defaultValue: "Create and link" })}
            </button>
            <button type="button" className={btnCls} disabled={busy} onClick={onCancel}>
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
